import React, { createContext, useContext, useEffect, useState } from "react";

import { DriverGetQueuePositionData, SettingsCompanyApi, driverCheckInServices, useDriverGetQueuePosition } from "@/domain";
import { useSession } from "@/hooks/@custom/useSession";
import { useGetIpQuery } from "@/store/apis/ipify";
import { useGetSettingsByCompanyQuery } from "@/store/apis/nextcargo";

import { useLoader } from "./loader";

type SettingsCompany = SettingsCompanyApi;

interface DriverCheckIn {
  id: number;
  driverQueueId: number;
  driverCheckInActive: boolean;
  checkInCreatedAt: string;
}

interface DriverContextData {
  isDriverIPAuthorized: boolean | null;
  settingsCompany?: SettingsCompany;
  driverCheckIn: DriverCheckIn | null;
  driverId?: string | null;
  driverCompanyId?: string;
  driverCompanyName: string | undefined;
  driverFirstName: string | undefined;
  isLoading: boolean;
  fetchCheckInActive: () => Promise<void>;
  updateCheckin: (checkin: DriverCheckIn | null) => void;
  driverQueuePosition: DriverGetQueuePositionData;
}

const DriverContext = createContext({} as DriverContextData);

function DriverProvider({ children }: { children: React.ReactNode }) {
  const { driver } = useSession();
  const driverCompanyId = driver?.company.id ? String(driver?.company.id) : undefined;
  const driverId = driver?.id ? String(driver?.id) : undefined;

  const { data: settingsCompany, isLoading: isLoadingSettings } = useGetSettingsByCompanyQuery({ id: Number(driverCompanyId) }, { skip: !driverCompanyId });
  const { data: ipifyData, isLoading: isLoadingIp } = useGetIpQuery();

  const allowedIps = (settingsCompany?.checkinIp ?? "")
    .split(",")
    .map((ip) => ip.trim())
    .filter((ip) => ip.length > 0);
  const isCurrentDriverAllowed = ipifyData?.ip ? allowedIps.includes(ipifyData.ip) : false;

  /* ######################################################################################### */

  const { isLoading } = useLoader();
  const driverQueuePosition = useDriverGetQueuePosition(driverId ?? "");

  const [driverCheckIn, setDriverCheckIn] = useState<DriverCheckIn | null>(null);

  const driverName = driver?.name;
  const driverCompanyName = driver?.company.name;
  const driverFirstName = driverName?.split(" ")[0];

  async function fetchCheckInActive() {
    if (!driverCompanyId) return;

    const checkInsActives = await driverCheckInServices.getCheckInActives(driverCompanyId);

    const driverCheckIn = checkInsActives.find((item) => item?.queue?.driver?.id.toString() === driverId);

    if (!driverCheckIn) return;

    setDriverCheckIn({
      id: driverCheckIn.id,
      driverQueueId: driverCheckIn.queue.id,
      driverCheckInActive: driverCheckIn.isPresent,
      checkInCreatedAt: driverCheckIn.updatedAt
    });
  }

  function updateCheckin(checkin: DriverCheckIn | null) {
    setDriverCheckIn(checkin);
  }

  useEffect(() => {
    fetchCheckInActive();
  }, [driverCompanyId]);

  return (
    <DriverContext.Provider
      value={{
        isLoading: isLoading || isLoadingSettings || isLoadingIp,
        isDriverIPAuthorized: isCurrentDriverAllowed,
        driverCheckIn,
        settingsCompany,
        driverId,
        driverCompanyId,
        driverCompanyName,
        driverFirstName,
        fetchCheckInActive,
        updateCheckin,
        driverQueuePosition
      }}
    >
      {children}
    </DriverContext.Provider>
  );
}

const useDriver = () => {
  const context = useContext(DriverContext);
  if (!context) {
    throw new Error("useDriver must be used within a DriverProvider");
  }
  return context;
};

export { DriverProvider, useDriver };
